import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Form,
  Input,
  InputNumber,
  Select,
  Statistic,
  Table,
  Tag,
  Tooltip,
  message,
} from "antd";
import { DownloadOutlined, ReloadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  Blank,
  DirectoryWrapper,
  SecondaryName,
  StatCard,
  StatsRow,
  TableContainer,
  ToolbarRow,
} from "./styled";
import {
  exportDirectory,
  getDirectoryHospitals,
  getDirectorySummary,
  updateDirectoryHospital,
} from "../../api/directory";
import { linkApi } from "../../common/ngok";

const { Search } = Input;

const YEAR_MIN = 1990;
const YEAR_MAX = 2100;
const UNSET_YEAR = "None";

const AWARD_COLOR = { awarded: "green", expired: "red", none: "default" };

// Names in the database are stored decomposed (NFD) while typed text is NFC
const normalize = (value) =>
  String(value ?? "").normalize("NFC").toLowerCase();
const matches = (text, search) => normalize(text).includes(normalize(search));

const yearToNumber = (value) =>
  value === null || value === undefined || value === UNSET_YEAR || value === ""
    ? null
    : Number(value);

// Form values for one row: empty choices become "", unset years become null
const toFormValues = (record) => ({
  ownership: record.ownership || "",
  specialty: record.specialty || "",
  award_status: record.award_status || "none",
  year_joined: record.year_joined ?? null,
  est_births_per_year: record.est_births_per_year ?? null,
  last_awarded_year: yearToNumber(record.last_awarded_year),
  note: record.note || "",
});

// Only the fields whose value differs from the stored row, in API form
const changedFields = (record, values) => {
  const before = toFormValues(record);
  const payload = {};
  Object.keys(values).forEach((key) => {
    const next = values[key] === undefined ? null : values[key];
    const prev = before[key];
    if ((prev ?? "") === (next ?? "")) return;
    if (key === "last_awarded_year") {
      payload[key] = next === null ? UNSET_YEAR : String(next);
    } else if (key === "ownership" || key === "specialty") {
      payload[key] = next === "" ? null : next;
    } else {
      payload[key] = next;
    }
  });
  return payload;
};

const Dash = () => <Blank>—</Blank>;

const EditableCell = ({ editing, dataIndex, inputNode, children, ...rest }) => (
  <td {...rest}>
    {editing ? (
      <Form.Item name={dataIndex} style={{ margin: 0 }}>
        {inputNode}
      </Form.Item>
    ) : (
      children
    )}
  </td>
);

const HospitalDirectoryPage = () => {
  const { t } = useTranslation();
  const language = useSelector((state) => state?.auth?.language);
  const [form] = Form.useForm();

  const [hospitals, setHospitals] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [provinceFilter, setProvinceFilter] = useState("all");
  const [awardFilter, setAwardFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const [list, stats] = await Promise.all([
        getDirectoryHospitals(),
        getDirectorySummary(),
      ]);
      if (list?.status !== "successful") throw new Error("load failed");
      setHospitals(list.hospitals || []);
      setSummary(stats?.status === "successful" ? stats : null);
    } catch (e) {
      message.error(t("directory.loadError"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshSummary = async () => {
    try {
      const stats = await getDirectorySummary();
      if (stats?.status === "successful") setSummary(stats);
    } catch (e) {
      // the table already shows the saved row; a stale count is acceptable
    }
  };

  const provinceOptions = useMemo(() => {
    const seen = new Map();
    hospitals.forEach((h) => {
      if (!seen.has(h.province_code)) seen.set(h.province_code, h.province_name);
    });
    return [...seen.entries()]
      .sort((a, b) => String(a[1]).localeCompare(String(b[1])))
      .map(([code, name]) => ({ value: String(code), label: name }));
  }, [hospitals]);

  const filtered = useMemo(
    () =>
      hospitals.filter((h) => {
        if (provinceFilter !== "all" && String(h.province_code) !== provinceFilter)
          return false;
        if (awardFilter !== "all" && (h.award_status || "none") !== awardFilter)
          return false;
        if (!search) return true;
        return (
          matches(h.name, search) ||
          matches(h.display_name, search) ||
          matches(h.old_name, search) ||
          matches(h.code, search) ||
          matches(h.province_name, search)
        );
      }),
    [hospitals, provinceFilter, awardFilter, search]
  );

  const isEditing = (record) => record.code === editingCode;

  const edit = (record) => {
    form.setFieldsValue(toFormValues(record));
    setEditingCode(record.code);
  };

  const cancel = () => setEditingCode(null);

  const save = async (record) => {
    const values = await form.validateFields();
    const payload = changedFields(record, values);
    if (Object.keys(payload).length === 0) {
      setEditingCode(null);
      return;
    }
    setSaving(true);
    try {
      const res = await updateDirectoryHospital(record.code, payload);
      if (res?.status === "successful" && res.hospital) {
        setHospitals((rows) =>
          rows.map((r) => (r.code === record.code ? res.hospital : r))
        );
        setEditingCode(null);
        message.success(t("directory.saved"));
        refreshSummary();
      } else {
        const detail = res?.detail || res?.data?.detail;
        message.error(detail || t("directory.saveError"));
      }
    } catch (e) {
      message.error(t("directory.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const onExport = async () => {
    setExporting(true);
    try {
      const res = await exportDirectory(language);
      const path = typeof res?.data === "string" ? res.data : res?.data?.url;
      if (res?.status === "successful" && path) {
        window.open(`${linkApi}/${String(path).replace(/^\/+/, "")}`);
      } else {
        message.error(t("directory.exportError"));
      }
    } catch (e) {
      message.error(t("directory.exportError"));
    } finally {
      setExporting(false);
    }
  };

  const ownershipOptions = [
    { value: "", label: t("directory.unset") },
    { value: "public", label: t("directory.ownershipPublic") },
    { value: "private", label: t("directory.ownershipPrivate") },
  ];
  const specialtyOptions = [
    { value: "", label: t("directory.unset") },
    { value: "specialised", label: t("directory.specialtySpecialised") },
    { value: "general", label: t("directory.specialtyGeneral") },
  ];
  const awardOptions = [
    { value: "none", label: t("directory.awardNone") },
    { value: "awarded", label: t("directory.awardAwarded") },
    { value: "expired", label: t("directory.awardExpired") },
  ];
  const labelOf = (options, value) =>
    options.find((o) => o.value === (value || ""))?.label;

  const yearInput = (
    <InputNumber min={YEAR_MIN} max={YEAR_MAX} precision={0} style={{ width: 90 }} />
  );

  const columns = [
    {
      title: t("directory.code"),
      dataIndex: "code",
      key: "code",
      width: 70,
      fixed: "left",
      sorter: (a, b) => a.code - b.code,
    },
    {
      title: t("directory.hospital"),
      key: "name",
      width: 240,
      fixed: "left",
      sorter: (a, b) =>
        (a.display_name || a.name || "").localeCompare(b.display_name || b.name || ""),
      render: (_, r) => (
        <div>
          <div>{r.display_name || r.name}</div>
          {r.old_name && r.old_name !== "None" && r.old_name !== (r.display_name || r.name) && (
            <SecondaryName>{r.old_name}</SecondaryName>
          )}
        </div>
      ),
    },
    {
      title: t("directory.province"),
      dataIndex: "province_name",
      key: "province_name",
      width: 140,
      sorter: (a, b) => (a.province_name || "").localeCompare(b.province_name || ""),
    },
    {
      title: t("directory.ownership"),
      dataIndex: "ownership",
      key: "ownership",
      width: 120,
      editable: true,
      inputNode: <Select options={ownershipOptions} style={{ width: 110 }} />,
      render: (v) => (v ? labelOf(ownershipOptions, v) : <Dash />),
    },
    {
      title: t("directory.specialty"),
      dataIndex: "specialty",
      key: "specialty",
      width: 120,
      editable: true,
      inputNode: <Select options={specialtyOptions} style={{ width: 110 }} />,
      render: (v) => (v ? labelOf(specialtyOptions, v) : <Dash />),
    },
    {
      title: t("directory.yearJoined"),
      dataIndex: "year_joined",
      key: "year_joined",
      width: 100,
      editable: true,
      inputNode: yearInput,
      sorter: (a, b) => (a.year_joined || 0) - (b.year_joined || 0),
      render: (v) => (v ? v : <Dash />),
    },
    {
      title: t("directory.estBirths"),
      dataIndex: "est_births_per_year",
      key: "est_births_per_year",
      width: 110,
      editable: true,
      inputNode: <InputNumber min={0} precision={0} style={{ width: 100 }} />,
      sorter: (a, b) => (a.est_births_per_year || 0) - (b.est_births_per_year || 0),
      render: (v) => (v !== null && v !== undefined ? v.toLocaleString() : <Dash />),
    },
    {
      title: t("directory.awardStatus"),
      dataIndex: "award_status",
      key: "award_status",
      width: 140,
      editable: true,
      inputNode: <Select options={awardOptions} style={{ width: 130 }} />,
      render: (v) => (
        <Tag color={AWARD_COLOR[v || "none"]}>{labelOf(awardOptions, v || "none")}</Tag>
      ),
    },
    {
      title: t("directory.lastAwardedYear"),
      dataIndex: "last_awarded_year",
      key: "last_awarded_year",
      width: 110,
      editable: true,
      inputNode: yearInput,
      render: (v) => (v && v !== UNSET_YEAR ? v : <Dash />),
    },
    {
      title: (
        <Tooltip title={t("directory.nextRecheckHint")}>
          {t("directory.nextRecheckYear")}
        </Tooltip>
      ),
      dataIndex: "next_recheck_year",
      key: "next_recheck_year",
      width: 110,
      render: (v) => (v && v !== UNSET_YEAR ? v : <Dash />),
    },
    {
      title: t("directory.note"),
      dataIndex: "note",
      key: "note",
      width: 200,
      editable: true,
      inputNode: <Input maxLength={1024} />,
      render: (v) => (v ? v : <Dash />),
    },
    {
      title: t("directory.actions"),
      key: "actions",
      width: 130,
      fixed: "right",
      render: (_, record) =>
        isEditing(record) ? (
          <>
            <Button type="link" size="small" loading={saving} onClick={() => save(record)}>
              {t("directory.save")}
            </Button>
            <Button type="link" size="small" disabled={saving} onClick={cancel}>
              {t("directory.cancel")}
            </Button>
          </>
        ) : (
          <Button
            type="link"
            size="small"
            disabled={editingCode !== null}
            onClick={() => edit(record)}
          >
            {t("directory.edit")}
          </Button>
        ),
    },
  ];

  const mergedColumns = columns.map((col) =>
    col.editable
      ? {
          ...col,
          onCell: (record) => ({
            record,
            dataIndex: col.dataIndex,
            inputNode: col.inputNode,
            editing: isEditing(record),
          }),
        }
      : col
  );

  const stat = (key, value) => (
    <StatCard key={key}>
      <Statistic title={t(`directory.${key}`)} value={value ?? 0} />
    </StatCard>
  );

  return (
    <DirectoryWrapper>
      {summary && (
        <StatsRow>
          {stat("statTotal", summary.total)}
          {stat("statAwarded", summary.by_award_status?.awarded)}
          {stat("statExpired", summary.by_award_status?.expired)}
          {stat("statMissingOwnership", summary.by_ownership?.missing)}
          {stat("statMissingSpecialty", summary.by_specialty?.missing)}
          {stat("statMissingYearJoined", summary.missing_year_joined)}
          {stat("statMissingBirths", summary.missing_est_births)}
        </StatsRow>
      )}
      <ToolbarRow>
        <Select
          aria-label={t("directory.province")}
          value={provinceFilter}
          onChange={setProvinceFilter}
          style={{ width: 200 }}
          showSearch
          optionFilterProp="label"
          options={[{ value: "all", label: t("directory.allProvinces") }, ...provinceOptions]}
        />
        <Select
          aria-label={t("directory.awardStatus")}
          value={awardFilter}
          onChange={setAwardFilter}
          style={{ width: 180 }}
          options={[{ value: "all", label: t("directory.allAwardStatuses") }, ...awardOptions]}
        />
        <Search
          placeholder={t("directory.searchPlaceholder")}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 260 }}
          allowClear
        />
        <Button icon={<ReloadOutlined />} onClick={load} disabled={loading}>
          {t("directory.refresh")}
        </Button>
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={onExport}
          loading={exporting}
        >
          {t("directory.exportExcel")}
        </Button>
        <span style={{ marginLeft: "auto", color: "#888" }}>
          {filtered.length} / {hospitals.length} {t("directory.rows")}
        </span>
      </ToolbarRow>
      <TableContainer>
        <Form form={form} component={false}>
          <Table
            components={{ body: { cell: EditableCell } }}
            dataSource={filtered}
            columns={mergedColumns}
            rowKey="code"
            size="small"
            loading={loading}
            scroll={{ x: 1600 }}
            locale={{ emptyText: t("directory.empty") }}
            pagination={{
              defaultPageSize: 50,
              showSizeChanger: true,
              pageSizeOptions: ["20", "50", "100", "200"],
            }}
          />
        </Form>
      </TableContainer>
    </DirectoryWrapper>
  );
};

export default HospitalDirectoryPage;
